import { Button, Grid, Paper, TextField, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getLastParameter,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";
const server = EnvSettings.server;

const getEditorValue = (deviceSettingsFile) => {
  if (!deviceSettingsFile) {
    return "";
  }
  if (
    deviceSettingsFile.data !== undefined &&
    deviceSettingsFile.data !== null
  ) {
    if (typeof deviceSettingsFile.data === "string") {
      try {
        return JSON.stringify(JSON.parse(deviceSettingsFile.data), null, 2);
      } catch (error) {
        return deviceSettingsFile.data;
      }
    }
    return JSON.stringify(deviceSettingsFile.data, null, 2);
  }
  return deviceSettingsFile.rawdata || "";
};

const formatDateTime = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
};

const getLineNumberFromSelection = (text, selectionStart) =>
  text.slice(0, selectionStart).split("\n").length - 1;

const removeLeadingClosers = (line) => {
  let rest = line.trim();
  while (rest.startsWith("}") || rest.startsWith("]")) {
    rest = rest.slice(1).trim();
    if (rest.startsWith(",")) {
      rest = rest.slice(1).trim();
    }
  }
  return rest;
};

const getPathAtLine = (text, lineNumber) => {
  const lines = text.split("\n");
  const stack = [];
  for (let i = 0; i <= lineNumber && i < lines.length; i += 1) {
    const trimmedOriginal = lines[i].trim();
    if (!trimmedOriginal) {
      continue;
    }

    let trimmed = removeLeadingClosers(trimmedOriginal);
    const keyMatch = trimmed.match(/^"((?:\\.|[^"\\])+)":\s*(.*)$/);

    if (keyMatch) {
      const key = keyMatch[1].replace(/\\"/g, '"');
      const valuePart = keyMatch[2].trim();
      const path = [...stack, key];
      if (i === lineNumber) {
        return path;
      }
      if (valuePart.startsWith("{") || valuePart.startsWith("[")) {
        stack.push(key);
      }
      continue;
    }

    if (trimmedOriginal.startsWith("}") || trimmedOriginal.startsWith("]")) {
      stack.pop();
    }

    if (i === lineNumber) {
      return [...stack];
    }
  }
  return [...stack];
};

const resolveLocalRef = (ref, rootSchema) => {
  if (!ref || !ref.startsWith("#/")) {
    return null;
  }
  const parts = ref
    .slice(2)
    .split("/")
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"));
  let current = rootSchema;
  for (const part of parts) {
    if (!current || typeof current !== "object") {
      return null;
    }
    current = current[part];
  }
  return current || null;
};

const derefNode = (node, rootSchema, depth = 0) => {
  if (!node || typeof node !== "object" || depth > 20 || !node.$ref) {
    return node;
  }
  const resolved = resolveLocalRef(node.$ref, rootSchema);
  if (!resolved) {
    return node;
  }
  const derefResolved = derefNode(resolved, rootSchema, depth + 1);
  const merged = {
    ...(derefResolved && typeof derefResolved === "object"
      ? derefResolved
      : {}),
    ...node,
  };
  delete merged.$ref;
  return merged;
};

const findPropertyNode = (node, key, rootSchema, depth = 0) => {
  if (!node || depth > 20) {
    return null;
  }
  const resolved = derefNode(node, rootSchema);
  if (!resolved || typeof resolved !== "object") {
    return null;
  }
  if (resolved.properties && resolved.properties[key]) {
    return resolved.properties[key];
  }
  if (resolved.items && typeof resolved.items === "object") {
    const itemNode = derefNode(resolved.items, rootSchema);
    if (itemNode && itemNode.properties && itemNode.properties[key]) {
      return itemNode.properties[key];
    }
  }
  if (
    resolved.patternProperties &&
    typeof resolved.patternProperties === "object"
  ) {
    const patternEntries = Object.entries(resolved.patternProperties);
    for (const [pattern, schemaNode] of patternEntries) {
      try {
        if (new RegExp(pattern).test(key)) {
          return schemaNode;
        }
      } catch (error) {
        // Ignore invalid schema regex patterns in UI helper logic.
      }
    }
  }
  if (
    resolved.additionalProperties &&
    typeof resolved.additionalProperties === "object"
  ) {
    return resolved.additionalProperties;
  }
  const combiners = ["allOf", "oneOf", "anyOf"];
  for (const combiner of combiners) {
    if (Array.isArray(resolved[combiner])) {
      for (const sub of resolved[combiner]) {
        const found = findPropertyNode(sub, key, rootSchema, depth + 1);
        if (found) {
          return found;
        }
      }
    }
  }
  return null;
};

const getSchemaInfoAtPath = (rootSchema, path) => {
  if (!rootSchema || !Array.isArray(path)) {
    return null;
  }
  let current = rootSchema;
  let required = false;
  for (let i = 0; i < path.length; i += 1) {
    const segment = path[i];
    const parent = derefNode(current, rootSchema);
    if (!parent) {
      return null;
    }
    if (i === path.length - 1) {
      if (Array.isArray(parent.required) && parent.required.includes(segment)) {
        required = true;
      } else if (parent.items && typeof parent.items === "object") {
        const itemNode = derefNode(parent.items, rootSchema);
        required =
          !!itemNode &&
          Array.isArray(itemNode.required) &&
          itemNode.required.includes(segment);
      } else {
        required = false;
      }
    }
    const next = findPropertyNode(parent, segment, rootSchema);
    if (!next) {
      return null;
    }
    current = next;
  }
  return {
    node: derefNode(current, rootSchema),
    required,
  };
};

const collectSchemaNodes = (node, rootSchema, depth = 0) => {
  if (!node || depth > 10) {
    return [];
  }
  const resolved = derefNode(node, rootSchema, 0);
  if (!resolved || typeof resolved !== "object") {
    return [];
  }
  let collected = [resolved];
  const combiners = ["allOf", "oneOf", "anyOf"];
  for (const combiner of combiners) {
    if (Array.isArray(resolved[combiner])) {
      resolved[combiner].forEach((sub) => {
        collected = [
          ...collected,
          ...collectSchemaNodes(sub, rootSchema, depth + 1),
        ];
      });
    }
  }
  return collected;
};

const getNodeRules = (rootSchema, nodeInfo) => {
  if (!rootSchema || !nodeInfo || !nodeInfo.node) {
    return null;
  }
  const nodes = collectSchemaNodes(nodeInfo.node, rootSchema);

  const types = Array.from(
    new Set(
      nodes.map((n) => n.type).filter((type) => typeof type === "string"),
    ),
  );
  const enumValues = Array.from(
    new Set(
      nodes
        .flatMap((n) => (Array.isArray(n.enum) ? n.enum : []))
        .map((value) => JSON.stringify(value)),
    ),
  ).map((value) => JSON.parse(value));

  const pickFirst = (key) =>
    nodes.find((node) => node[key] !== undefined && node[key] !== null)?.[key];

  const requiredSubkeys = new Set();
  const propertiesMap = new Map();
  const patternSubkeys = new Set();
  let allowsAdditionalSubkeys = false;

  nodes.forEach((node) => {
    if (Array.isArray(node.required)) {
      node.required.forEach((requiredKey) => requiredSubkeys.add(requiredKey));
    }
    if (node.properties && typeof node.properties === "object") {
      Object.keys(node.properties).forEach((propertyKey) =>
        propertiesMap.set(propertyKey, true),
      );
    }
    if (node.items && typeof node.items === "object") {
      const itemNode = derefNode(node.items, rootSchema);
      if (itemNode && Array.isArray(itemNode.required)) {
        itemNode.required.forEach((requiredKey) =>
          requiredSubkeys.add(requiredKey),
        );
      }
      if (
        itemNode &&
        itemNode.properties &&
        typeof itemNode.properties === "object"
      ) {
        Object.keys(itemNode.properties).forEach((propertyKey) =>
          propertiesMap.set(propertyKey, true),
        );
      }
    }
    if (node.patternProperties && typeof node.patternProperties === "object") {
      Object.keys(node.patternProperties).forEach((patternKey) =>
        patternSubkeys.add(patternKey),
      );
    }
    if (node.additionalProperties === true) {
      allowsAdditionalSubkeys = true;
    }
  });

  const subkeys = Array.from(propertiesMap.keys()).map((name) => ({
    name,
    required: requiredSubkeys.has(name),
  }));

  return {
    title: pickFirst("title"),
    description: pickFirst("description"),
    defaultValue: pickFirst("default"),
    minimum: pickFirst("minimum"),
    maximum: pickFirst("maximum"),
    minItems: pickFirst("minItems"),
    maxItems: pickFirst("maxItems"),
    minLength: pickFirst("minLength"),
    maxLength: pickFirst("maxLength"),
    pattern: pickFirst("pattern"),
    types,
    enumValues,
    required: nodeInfo.required,
    subkeys,
    patternSubkeys: Array.from(patternSubkeys),
    allowsAdditionalSubkeys,
  };
};

const getValueAtPath = (obj, path) => {
  if (!obj || !Array.isArray(path)) {
    return undefined;
  }
  let current = obj;
  for (const segment of path) {
    if (!current || typeof current !== "object") {
      return undefined;
    }
    current = current[segment];
  }
  return current;
};

const cloneDefaultValue = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== "object") {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const getTypeList = (schemaNode) => {
  if (!schemaNode || typeof schemaNode !== "object") {
    return [];
  }
  if (typeof schemaNode.type === "string") {
    return [schemaNode.type];
  }
  if (Array.isArray(schemaNode.type)) {
    return schemaNode.type.filter((typeValue) => typeof typeValue === "string");
  }
  return [];
};

const getFallbackPrimitive = (schemaNode, rootSchema, depth = 0) => {
  if (!schemaNode || typeof schemaNode !== "object" || depth > 20) {
    return undefined;
  }
  const node = derefNode(schemaNode, rootSchema);
  if (!node || typeof node !== "object") {
    return undefined;
  }
  if (node.const !== undefined) {
    return cloneDefaultValue(node.const);
  }
  if (Array.isArray(node.enum) && node.enum.length) {
    return cloneDefaultValue(node.enum[0]);
  }
  const combiners = ["oneOf", "anyOf", "allOf"];
  for (const combiner of combiners) {
    if (Array.isArray(node[combiner])) {
      for (const subNode of node[combiner]) {
        const candidate = getFallbackPrimitive(subNode, rootSchema, depth + 1);
        if (candidate !== undefined) {
          return candidate;
        }
      }
    }
  }
  const typeList = getTypeList(node);
  if (typeList.includes("integer") || typeList.includes("number")) {
    if (typeof node.minimum === "number") {
      return node.minimum;
    }
    return 0;
  }
  if (typeList.includes("string")) {
    if (typeof node.minLength === "number" && node.minLength > 0) {
      return "default";
    }
    return "";
  }
  if (typeList.includes("boolean")) {
    return false;
  }
  return undefined;
};

const buildDefaultFromSchema = (schemaNode, rootSchema, depth = 0) => {
  if (!schemaNode || depth > 20) {
    return undefined;
  }
  const node = derefNode(schemaNode, rootSchema);
  if (!node || typeof node !== "object") {
    return undefined;
  }
  if (node.default !== undefined) {
    return cloneDefaultValue(node.default);
  }
  if (node.const !== undefined) {
    return cloneDefaultValue(node.const);
  }
  if (Array.isArray(node.enum) && node.enum.length) {
    return cloneDefaultValue(node.enum[0]);
  }

  if (Array.isArray(node.allOf) && node.allOf.length) {
    const mergedObject = {};
    let hasObjectPart = false;
    for (const subNode of node.allOf) {
      const partDefault = buildDefaultFromSchema(
        subNode,
        rootSchema,
        depth + 1,
      );
      if (
        partDefault !== undefined &&
        partDefault !== null &&
        typeof partDefault === "object" &&
        !Array.isArray(partDefault)
      ) {
        Object.assign(mergedObject, partDefault);
        hasObjectPart = true;
      } else if (partDefault !== undefined) {
        return partDefault;
      }
    }
    if (hasObjectPart) {
      return mergedObject;
    }
  }

  const branchCombiners = ["oneOf", "anyOf"];
  for (const combiner of branchCombiners) {
    if (Array.isArray(node[combiner])) {
      for (const subNode of node[combiner]) {
        const candidate = buildDefaultFromSchema(
          subNode,
          rootSchema,
          depth + 1,
        );
        if (candidate !== undefined) {
          return candidate;
        }
      }
    }
  }

  const typeList = getTypeList(node);
  const isObjectType =
    typeList.includes("object") ||
    (node.properties && typeof node.properties === "object");
  if (isObjectType) {
    const obj = {};
    if (node.properties && typeof node.properties === "object") {
      for (const [key, subNode] of Object.entries(node.properties)) {
        const childDefault = buildDefaultFromSchema(
          subNode,
          rootSchema,
          depth + 1,
        );
        if (childDefault !== undefined) {
          obj[key] = childDefault;
        }
      }
    }
    if (Array.isArray(node.required) && node.properties) {
      node.required.forEach((requiredKey) => {
        if (obj[requiredKey] !== undefined || !node.properties[requiredKey]) {
          return;
        }
        const fallback = getFallbackPrimitive(
          node.properties[requiredKey],
          rootSchema,
          depth + 1,
        );
        if (fallback !== undefined) {
          obj[requiredKey] = fallback;
        }
      });
    }
    return obj;
  }

  const isArrayType = typeList.includes("array") || !!node.items;
  if (isArrayType) {
    const minItems = typeof node.minItems === "number" ? node.minItems : 0;
    const arr = [];
    if (minItems > 0) {
      for (let i = 0; i < minItems; i += 1) {
        let itemDefault = undefined;
        if (node.items && typeof node.items === "object") {
          itemDefault = buildDefaultFromSchema(
            node.items,
            rootSchema,
            depth + 1,
          );
          if (itemDefault === undefined) {
            itemDefault = getFallbackPrimitive(
              node.items,
              rootSchema,
              depth + 1,
            );
          }
        }
        arr.push(itemDefault !== undefined ? itemDefault : {});
      }
    }
    return arr;
  }

  return getFallbackPrimitive(node, rootSchema, depth + 1);
};

function JSONEditor({ deviceSettingsFile }) {
  const dispatch = useDispatch();
  const [editorValue, setEditorValue] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState("");
  const [isSchemaLoading, setIsSchemaLoading] = useState(false);
  const [cursorPath, setCursorPath] = useState([]);

  useEffect(() => {
    const value = getEditorValue(deviceSettingsFile);
    setEditorValue(value);
    setInitialValue(value);
    setError("");
    setSaveMessage("");
    setCursorPath([]);
  }, [deviceSettingsFile]);

  useEffect(() => {
    const fetchSchema = async () => {
      if (
        !deviceSettingsFile ||
        !deviceSettingsFile.name ||
        !deviceSettingsFile.DeviceId
      ) {
        setSchema(null);
        setSchemaError("");
        return;
      }
      setIsSchemaLoading(true);
      setSchemaError("");
      try {
        const requestOptions = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: getToken(),
            deviceid: deviceSettingsFile.DeviceId,
            name: deviceSettingsFile.name,
          }),
        };
        const res = await fetch(
          server + "/get_device_settings_schema",
          requestOptions,
        );
        const result = await res.json();
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setSchema(result.schema || null);
        } else {
          setSchema(null);
          setSchemaError(result.message || "Schema loading failed");
        }
      } catch (schemaFetchError) {
        setSchema(null);
        setSchemaError(schemaFetchError.message || "Schema loading failed");
      } finally {
        setIsSchemaLoading(false);
      }
    };
    fetchSchema();
  }, [deviceSettingsFile, dispatch]);

  const updateCursorPath = (event) => {
    const target = event && event.target;
    if (!target || typeof target.selectionStart !== "number") {
      return;
    }
    const lineNumber = getLineNumberFromSelection(
      target.value,
      target.selectionStart,
    );
    setCursorPath(getPathAtLine(target.value, lineNumber));
  };

  const onChange = (event) => {
    const value = event.target.value;
    setEditorValue(value);
    setSaveMessage("");
    updateCursorPath(event);
    try {
      JSON.parse(value);
      setError("");
    } catch (parseError) {
      setError(parseError.message);
    }
  };

  const formatJson = () => {
    try {
      const formatted = JSON.stringify(JSON.parse(editorValue), null, 2);
      setEditorValue(formatted);
      setError("");
      setCursorPath([]);
    } catch (parseError) {
      setError(parseError.message);
    }
  };

  const cancelChanges = () => {
    setEditorValue(initialValue);
    setSaveMessage("");
    setCursorPath([]);
    try {
      JSON.parse(initialValue || "");
      setError("");
    } catch (parseError) {
      setError(parseError.message);
    }
  };

  const factoryReset = () => {
    if (!schema) {
      setSaveMessage("Schema is not available for factory reset");
      return;
    }
    const defaults = buildDefaultFromSchema(schema, schema);
    const resetValue = JSON.stringify(
      defaults !== undefined ? defaults : {},
      null,
      2,
    );
    setEditorValue(resetValue);
    setSaveMessage("Factory defaults loaded (not saved)");
    setCursorPath([]);
    setError("");
  };

  const save = async () => {
    if (error || !editorValue) {
      return;
    }
    setIsSaving(true);
    setSaveMessage("");
    try {
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          deviceid: deviceSettingsFile.DeviceId,
          name: deviceSettingsFile.name,
          data: editorValue,
        }),
      };
      const res = await fetch(
        server + "/edit_device_settings_file",
        requestOptions,
      );
      const result = await res.json();
      requestErrorHandler(result, dispatch);
      if (!result.error) {
        setInitialValue(editorValue);
        setSaveMessage("Saved");
        dispatch(showPopupSnackbar(result));
        // Quick fix for the data persistence when switching the tabs
        window.location.reload();
      } else {
        setSaveMessage(result.message || "Save failed");
      }
    } catch (saveError) {
      setSaveMessage(saveError.message || "Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const schemaInfo = getSchemaInfoAtPath(schema, cursorPath);
  const rules = getNodeRules(schema, schemaInfo);
  const activePath =
    cursorPath && cursorPath.length ? `/${cursorPath.join("/")}` : "/";
  const updatedAtText = formatDateTime(deviceSettingsFile.updatedAt);
  const downloadedAtText = formatDateTime(deviceSettingsFile.downloadedAt);
  const updatedByText =
    deviceSettingsFile.lastModifier === "user"
      ? deviceSettingsFile.User?.fullName ||
        (deviceSettingsFile.UserId
          ? `User #${deviceSettingsFile.UserId}`
          : "User")
      : deviceSettingsFile.lastModifier === "device"
        ? "Device"
        : "Unknown";
  const downloadStateText =
    deviceSettingsFile.lastModifier === "user"
      ? downloadedAtText
        ? `Downloaded by device at ${downloadedAtText}`
        : "Not yet downloaded by device"
      : "Last update originated from device";
  let parsedEditorJson = null;
  try {
    parsedEditorJson = JSON.parse(editorValue);
  } catch (parseError) {}
  const currentValueAtPath = getValueAtPath(parsedEditorJson, cursorPath);
  const isArrayRule = !!(
    rules &&
    Array.isArray(rules.types) &&
    rules.types.includes("array")
  );
  const presentSubkeys =
    currentValueAtPath &&
    typeof currentValueAtPath === "object" &&
    !Array.isArray(currentValueAtPath)
      ? Object.keys(currentValueAtPath)
      : [];
  const arrayLength = Array.isArray(currentValueAtPath)
    ? currentValueAtPath.length
    : null;
  const hasTooFewItems =
    isArrayRule &&
    typeof rules?.minItems === "number" &&
    arrayLength !== null &&
    arrayLength < rules.minItems;
  const hasTooManyItems =
    isArrayRule &&
    typeof rules?.maxItems === "number" &&
    arrayLength !== null &&
    arrayLength > rules.maxItems;
  const missingRequiredSubkeys =
    !isArrayRule && rules && rules.subkeys
      ? rules.subkeys
          .filter(
            (subkey) =>
              subkey.required && !presentSubkeys.includes(subkey.name),
          )
          .map((subkey) => subkey.name)
      : [];
  const missingOptionalSubkeys =
    !isArrayRule && rules && rules.subkeys
      ? rules.subkeys
          .filter(
            (subkey) =>
              !subkey.required && !presentSubkeys.includes(subkey.name),
          )
          .map((subkey) => subkey.name)
      : [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper style={{ padding: 12 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                Updated: {updatedAtText || "Unknown"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                Updated by: {updatedByText}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                color={
                  deviceSettingsFile.lastModifier === "user" &&
                  !downloadedAtText
                    ? "error"
                    : "textPrimary"
                }
              >
                {downloadStateText}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <TextField
              value={editorValue}
              onChange={onChange}
              onClick={updateCursorPath}
              onKeyUp={updateCursorPath}
              onSelect={updateCursorPath}
              multiline
              rows={24}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button variant="outlined" color="primary" onClick={formatJson}>
                  Format JSON
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={factoryReset}
                  disabled={isSchemaLoading || !schema}
                >
                  Factory Reset
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={cancelChanges}
                  disabled={editorValue === initialValue}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={save}
                  disabled={isSaving || !!error || editorValue === initialValue}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {error ? (
            <Grid item>
              <Typography color="error">Invalid JSON: {error}</Typography>
            </Grid>
          ) : null}
          {saveMessage ? (
            <Grid item>
              <Typography>{saveMessage}</Typography>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper style={{ padding: 16, minHeight: 300 }}>
          <Typography variant="h6">Key rules</Typography>
          <Typography variant="body2">Cursor path: {activePath}</Typography>
          {isSchemaLoading ? (
            <Typography variant="body2" style={{ marginTop: 12 }}>
              Loading schema...
            </Typography>
          ) : null}
          {schemaError ? (
            <Typography color="error" style={{ marginTop: 12 }}>
              {schemaError}
            </Typography>
          ) : null}
          {!isSchemaLoading && !schemaError && !schema ? (
            <Typography variant="body2" style={{ marginTop: 12 }}>
              No schema found for this file.
            </Typography>
          ) : null}
          {!isSchemaLoading && !schemaError && schema && !rules ? (
            <Typography variant="body2" style={{ marginTop: 12 }}>
              No direct schema rule found for this cursor location.
            </Typography>
          ) : null}
          {!isSchemaLoading && !schemaError && rules ? (
            <Grid
              container
              direction="column"
              spacing={1}
              style={{ marginTop: 8 }}
            >
              {rules.title ? (
                <Grid item>
                  <Typography variant="subtitle2">{rules.title}</Typography>
                </Grid>
              ) : null}
              {rules.description ? (
                <Grid item>
                  <Typography variant="body2">{rules.description}</Typography>
                </Grid>
              ) : null}
              <Grid item>
                <Typography variant="body2">
                  Required: {rules.required ? "yes" : "no"}
                </Typography>
              </Grid>
              {rules.types && rules.types.length ? (
                <Grid item>
                  <Typography variant="body2">
                    Type: {rules.types.join(" | ")}
                  </Typography>
                </Grid>
              ) : null}
              {rules.enumValues && rules.enumValues.length ? (
                <Grid item>
                  <Typography variant="body2">
                    Allowed:{" "}
                    {rules.enumValues.map((v) => JSON.stringify(v)).join(", ")}
                  </Typography>
                </Grid>
              ) : null}
              {rules.minimum !== undefined || rules.maximum !== undefined ? (
                <Grid item>
                  <Typography variant="body2">
                    Range:{" "}
                    {rules.minimum !== undefined ? rules.minimum : "-inf"} to{" "}
                    {rules.maximum !== undefined ? rules.maximum : "+inf"}
                  </Typography>
                </Grid>
              ) : null}
              {rules.minItems !== undefined || rules.maxItems !== undefined ? (
                <Grid item>
                  <Typography variant="body2">
                    Item count:{" "}
                    {rules.minItems !== undefined ? rules.minItems : 0} to{" "}
                    {rules.maxItems !== undefined
                      ? rules.maxItems
                      : "unbounded"}
                  </Typography>
                </Grid>
              ) : null}
              {rules.minLength !== undefined ||
              rules.maxLength !== undefined ? (
                <Grid item>
                  <Typography variant="body2">
                    Length:{" "}
                    {rules.minLength !== undefined ? rules.minLength : 0} to{" "}
                    {rules.maxLength !== undefined
                      ? rules.maxLength
                      : "unbounded"}
                  </Typography>
                </Grid>
              ) : null}
              {rules.pattern ? (
                <Grid item>
                  <Typography variant="body2">
                    Pattern: {rules.pattern}
                  </Typography>
                </Grid>
              ) : null}
              {rules.defaultValue !== undefined ? (
                <Grid item>
                  <Typography variant="body2">
                    Default: {JSON.stringify(rules.defaultValue)}
                  </Typography>
                </Grid>
              ) : null}
              {/* {rules.subkeys && rules.subkeys.length ? (
                <Grid item>
                  <Typography variant="body2">
                    Possible subkeys:{" "}
                    {rules.subkeys
                      .map((subkey) =>
                        subkey.required ? `${subkey.name} (required)` : subkey.name,
                      )
                      .join(", ")}
                  </Typography>
                </Grid>
              ) : null} */}
              {/* {rules.patternSubkeys && rules.patternSubkeys.length ? (
                <Grid item>
                  <Typography variant="body2">
                    Pattern subkeys: {rules.patternSubkeys.join(", ")}
                  </Typography>
                </Grid>
              ) : null} */}
              {missingRequiredSubkeys.length ? (
                <Grid item>
                  <Typography color="error" variant="body2">
                    Missing required subkeys:{" "}
                    {missingRequiredSubkeys.join(", ")}
                  </Typography>
                </Grid>
              ) : null}
              {hasTooFewItems ? (
                <Grid item>
                  <Typography color="error" variant="body2">
                    Array has too few items: {arrayLength} (minimum{" "}
                    {rules.minItems})
                  </Typography>
                </Grid>
              ) : null}
              {hasTooManyItems ? (
                <Grid item>
                  <Typography color="error" variant="body2">
                    Array has too many items: {arrayLength} (maximum{" "}
                    {rules.maxItems})
                  </Typography>
                </Grid>
              ) : null}
              {missingOptionalSubkeys.length ? (
                <Grid item>
                  <Typography
                    variant="body2"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    Missing optional subkeys:{"\n"}
                    {missingOptionalSubkeys.join("\n")}
                  </Typography>
                </Grid>
              ) : null}
              {rules.allowsAdditionalSubkeys ? (
                <Grid item>
                  <Typography variant="body2">
                    Additional subkeys are allowed.
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default function DeviceSettingsFile({ deviceData, filename }) {
  const [crashlogs, setCrashlogs] = useState();
  const device_id = getLastParameter();

  const dispatch = useDispatch();

  useEffect(() => {
    const getSessions = () => {
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          deviceid: device_id,
        }),
      };
      var endpoint;
      if (filename.includes("recipe")) {
        endpoint = "/get_recipe_file";
      } else if (filename.includes("settings")) {
        endpoint = "/get_settings_file";
      }
      if (!endpoint) {
        return;
      }
      fetch(server + endpoint, requestOptions)
        .then((res) => res.json())
        .then((result) => {
          requestErrorHandler(result, dispatch);
          if (!result.error) {
            if (result.recipe) {
              setCrashlogs(JSON.stringify(result.recipe, undefined, 2));
              return;
            }
            if (result.settings) {
              setCrashlogs(JSON.stringify(result.settings, undefined, 2));
            }
          }
        });
    };
    if (deviceData) {
      if (
        !deviceData.DeviceSettingsFiles ||
        !deviceData.DeviceSettingsFiles.find((f) => f.name.includes(filename))
      ) {
        getSessions();
      } else {
        setCrashlogs(
          deviceData.DeviceSettingsFiles.find((f) => f.name.includes(filename)),
        );
      }
    }
  }, [device_id, dispatch, deviceData]);

  return crashlogs ? (
    <>
      <Grid container spacing={3} style={{ flexDirection: "row" }}>
        {typeof crashlogs === "string" ? (
          <Grid item>
            <Typography style={{ whiteSpace: "pre-wrap" }}>
              {crashlogs}
            </Typography>
          </Grid>
        ) : (
          <JSONEditor deviceSettingsFile={crashlogs} />
        )}
      </Grid>
    </>
  ) : null;
}
